<template>
	<section
		class="scene-trainer-diagram"
		v-bind:class="{
			'state-enabled': enabled,
			'state-ready': ready,
		}"
	>
		<ChalkyTrainerSceneGame
			ref="table"
			v-bind:ambientSound="true"
			v-bind:autoActivate="false"
			v-bind:autoStartAmbient="false"
			v-bind:autoStartMusic="false"
			v-bind:backgroundMusic="false"
			v-bind:debugMode="false"
			v-bind:environment="'Chalky'"
			v-bind:showEnglishMeter="true"
			v-bind:showFpsCounter="false"
			v-bind:showPowerMeter="true"
			v-bind:showTrails="true"
			v-bind:soundEnabled="true"
			v-bind:startInLowPower="false"
			v-bind:tableAppearance="'Chalky'"
			v-on:game:ready="Handle_OnGameReady"
		/>

		<!-- <ChalkyTrainerViewOverlayLowerThirdMosconiCup /> -->

		<ChalkyAuthenticationNavigation
			v-bind:authModel="$store.getters['authentication/authModel']"
			v-if="$store.getters.isAuthenticationEnabled && $store.getters.isLoginEnabled"
		/>

		<nav class="actions">
			<button
				id="btnTrainerBack"
				class="control round size-medium type-left background-transparent filter-invert"
				v-on:click="Handle_OnClickBack"
			>
				Back
			</button>

			<!-- We don't need this YET -->
			<button
				id="btnTrainerMenu"
				class="control round size-medium type-menu background-white"
				v-on:click="Handle_OnClickMenu"
			>
				Menu
			</button>
		</nav>

		<section class="user-guidance" v-if="enabled">
			<ChalkyUiCoachMarkSequencer
				id="tipTrainerTutorial"
				v-bind:arrow="true"
				v-bind:steps="[
					{
						content: 'Drag up and down to adjust shot power.',
						placement: 'left',
						position: 'center',
						target: '.view-powerenglish',
						theme: 'light',
						title: 'Power Meter',
					},
					{
						content: 'Tap to switch to English (spin) settings.',
						placement: 'left',
						position: 'center',
						target: '.view-powerenglish',
						theme: 'light',
						title: 'English',
					},
					{
						content: 'View mobile controls and Trainer settings.',
						placement: 'right',
						position: 'bottom',
						target: '#btnTrainerMenu',
						theme: 'light',
						title: 'Learn More',
					},
					{
						content: 'Back to the Pad layout.',
						placement: 'right',
						position: 'top',
						target: '#btnTrainerBack',
						theme: 'light',
						title: 'Back to Pad',
					},
				]"
			/>
		</section>

		<ChalkyUtilitySettingsPanel
			ref="settingsPanel"
			v-bind:tabs="settingsTabs"
			v-bind:value="settingsTab"
			v-on:input="Handle_OnCustomTabChange"
		>
			<!-- About -->
			<template v-slot:about>
				<div class="default-content">
					<h2 class="color-chalky-white">ChalkySticks Trainer</h2>
					<p>v{{ config.App.TRAINER_VERSION }}</p>

					<p class="type-large-paragraph" style="margin-top: 2rem">
						Visualize patterns. Experiment with angles.
						<br />
						Train your eye before you train your stroke.
					</p>

					<hr />

					<p>
						Have feedback?
						<a href="mailto:feedback@chalkysticks.com">We'd love to hear it</a>
					</p>
				</div>
			</template>

			<!-- Game Tab Content -->
			<template v-slot:actions>
				<div class="panel-quickactions default-content">
					<ChalkyTrainerViewButtonUndo>Undo</ChalkyTrainerViewButtonUndo>
					<ChalkyTrainerViewButtonRedo />
					<ChalkyTrainerViewButtonShuffle />
					<ChalkyTrainerViewButtonEnvironment />
					<ChalkyTrainerViewButtonTable />
					<!-- <ChalkyTrainerViewButtonRackEight /> -->
					<!-- <ChalkyTrainerViewButtonRackNine /> -->
					<!-- <ChalkyTrainerViewButtonRackTen /> -->
					<!-- <ChalkyTrainerViewButtonZoomIn /> -->
					<!-- <ChalkyTrainerViewButtonZoomOut /> -->
				</div>
			</template>

			<!-- Controls Tab Content -->
			<template v-slot:gameplay>
				<div class="default-content">
					<h3>Gameplay</h3>
					<p>Coming soon.</p>
				</div>
			</template>

			<!-- Controls Tab Content -->
			<template v-slot:equipment>
				<div class="default-content">
					<h3>Equipment</h3>
					<p>Coming soon.</p>
				</div>
			</template>

			<!-- Controls Tab Content -->
			<template v-slot:settings>
				<div class="default-content">
					<h3>Settings</h3>
					<p>Coming soon.</p>
				</div>
			</template>

			<!-- Help -->
			<template v-slot:help>
				<div class="default-content">
					<img src="@/Asset/image/graphic/trainer-help.svg" />
				</div>
			</template>
		</ChalkyUtilitySettingsPanel>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Scene for the 3D pool trainer with diagram integration, settings panel,
	 * and gameplay controls
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/Trainer
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneTrainerDiagram extends SceneBase {
		/**
		 * @type any
		 */
		@Ref('settingsPanel')
		readonly settingsPanel!: any;

		/**
		 * @type ChalkySticks.Model.Diagram | null
		 */
		@Prop()
		public diagramModel!: ChalkySticks.Model.Diagram;

		/**
		 * This is some hacky stuff to disable UI that might interfere
		 * with other overlapping views
		 *
		 * @type boolean
		 */
		@Prop({ default: false })
		public enabled!: boolean;

		/**
		 * Show the help tooltips
		 *
		 * @type boolean
		 */
		protected ready: boolean = true;

		/**
		 * Active tab for custom panel
		 *
		 * @type string
		 */
		protected settingsTab: string = 'Help';

		/**
		 * Custom tab configuration
		 *
		 * @type string[]
		 */
		protected settingsTabs: string[] = ['About', 'Help', 'Gameplay', 'Equipment', 'Settings'];

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('performance:lowpower:exit', this.Handle_OnPerformanceLowPowerChanged);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('performance:lowpower:exit', this.Handle_OnPerformanceLowPowerChanged);
		}

		/**
		 * @return Promise<void>
		 */
		protected loadBalls(): any {
			const customRack: any = [];
			let layout: any = this.diagramModel.getLayout();

			// If we dont have any balls, bail
			if (!layout?.balls) {
				return [];
			}

			// Format the balls in a way Trainer will understand
			layout.balls.forEach((layoutBall: any) => {
				const ball = this.getBallType(layoutBall.type);
				let x = layoutBall.hasOwnProperty('normalizedX') ? layoutBall.normalizedX : layoutBall.x;
				let y = layoutBall.hasOwnProperty('normalizedY') ? layoutBall.normalizedY : layoutBall.y;

				// Invert the numbers because we rotated the camera
				x = 1 - x;
				y = 1 - y;

				if (ball < 0) {
					return;
				}

				customRack.push({
					ball,
					x,
					y,
				});
			});

			return customRack;
		}

		/**
		 * @param string
		 * @return number
		 */
		protected getBallType(ballType: string): number {
			const number = ballType.match(/\d+/);

			if (ballType.includes('cue')) {
				return 0;
			}

			return number ? parseInt(number[0], 10) : -1;
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickBack(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.$emit('back');
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickMenu(e: Event): Promise<void> {
			e.preventDefault();

			this.settingsPanel.show();
		}

		/**
		 * Handle custom tab change
		 *
		 * @param string tab
		 * @return Promise<void>
		 */
		protected async Handle_OnCustomTabChange(tab: string): Promise<void> {
			this.settingsTab = tab;
		}

		/**
		 * Has an issue where it can't load twice
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnGameReady(): Promise<void> {
			this.ready = true;
		}

		/**
		 * Has an issue where it can't load twice
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnPerformanceLowPowerChanged(): Promise<void> {
			// Do nothing
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		@Watch('enabled')
		protected async Handle_OnWatchEnabled(e: Event): Promise<void> {
			const customRack = this.loadBalls() ?? [];

			ChalkySticks.Core.Event.Bus.dispatch('game.control:rackCustom', customRack);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-trainer-diagram {
		.authentication-navigation {
			position: absolute;
			right: var(--ui-page-margin);
			top: var(--ui-page-margin);
		}

		.scene-game {
			.view-power {
				--height: calc(100dvh - var(--padding) * 3 - 40px);

				bottom: var(--padding);
				right: var(--padding);
				top: auto;
			}

			.english-overlay .control {
				--size: 200px;
			}
		}

		.view-overlay-lowerthird-mosconicup {
			bottom: 0.5rem;
			font-size: 12px;
			left: 50%;
			max-width: 70dvw;
			position: absolute;
			transform: translate(-50%, 0);
			z-index: 2;
		}

		.panel-quickactions {
			display: grid;
			gap: 1rem;
			grid-auto-rows: auto;
			grid-template-columns: repeat(4, 1fr);

			button {
				--background-color: var(--chalky-blue);

				align-items: center;
				display: flex;
				font-size: 0.85em;
				font-weight: var(--font-weight-medium);
				gap: 0.5em;
				justify-content: center;
				text-transform: uppercase;
				width: 100%;
			}
		}

		.actions {
			height: 100%;
			left: 0;
			position: absolute;
			top: 0;
			z-index: 5;

			button {
				position: absolute;
			}

			#btnTrainerBack {
				left: var(--ui-page-margin);
				top: var(--ui-page-margin);
			}

			#btnTrainerMenu {
				bottom: var(--ui-page-margin);
				left: var(--ui-page-margin);
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.authentication-enabled .scene-trainer-diagram {
		.scene-game .view-powerenglish {
			top: 50px;
		}
	}

	.scene-trainer-diagram:not(.state-enabled) {
		.control-container {
			display: none;
		}
	}
</style>
